import React, { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import CompetitionService from "../services/CompetitionService";
import ParticipantService from "../services/ParticipantService";
import CreationModal from "../components/competitions/CreationModal";
import EditModal from "../components/competitions/EditModal";
import CompetitionCard from "../components/competitions/CompetitionCard";
import ManageParticipantsSection from "../components/competitions/ManageParticipantsSection";
import { PageHeader, ConfirmDialog } from "../components/admin";

function Competitions() {
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentCompetition, setCurrentCompetition] = useState(null);
  const [newCompetition, setNewCompetition] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "draft",
    category: ""
  });
  const [activeTab, setActiveTab] = useState("active");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);
  const [competitionToDelete, setCompetitionToDelete] = useState(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [selectedClubIds, setSelectedClubIds] = useState([]);
  const [selectedCoachIds, setSelectedCoachIds] = useState([]);
  const [selectedLifestyleIds, setSelectedLifestyleIds] = useState([]);
  const [selectedArtistIds, setSelectedArtistIds] = useState([]); // NEW
  const [selectedSongIds, setSelectedSongIds] = useState([]); // NEW
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [lifestyles, setLifestyles] = useState([]);
  const [artists, setArtists] = useState([]); // NEW
  const [songs, setSongs] = useState([]); // NEW
  const [isAddingParticipants, setIsAddingParticipants] = useState(false);
  const [competitionService, setCompetitionService] = useState(null);
  const [participantService, setParticipantService] = useState(null);

  // Updated categories to include artist and song
  const categories = [
    "coach",
    "player",
    "club",
    "model",
    "artist", // Replaced "music" with "artist"
    "song"    // Added "song" as a new category
  ];

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    console.log('Environment API URL:', apiUrl);
    if (!apiUrl) {
      console.error('API URL environment variable is not set');
      return;
    }
    setApiBaseUrl(apiUrl);
    setCompetitionService(new CompetitionService(apiUrl));
    setParticipantService(new ParticipantService(apiUrl));
    console.log('Setting API Base URL to:', apiUrl);
  }, []);

  useEffect(() => {
    if (competitionService) {
      fetchCompetitions();
    }
  }, [competitionService]);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const data = await competitionService.fetchCompetitions();
      
      // Fetch participants for each competition
      const competitionsWithParticipants = await Promise.all(
        data.map(async (competition) => {
          try {
            const participants = await competitionService.fetchCompetitionParticipants(
              competition.id,
              competition.category
            );
            return {
              ...competition,
              participants
            };
          } catch (err) {
            console.error(`Error fetching participants for competition ${competition.id}:`, err);
            return {
              ...competition,
              participants: []
            };
          }
        })
      );
      
      setCompetitions(competitionsWithParticipants);
    } catch (err) {
      console.error("Error fetching competitions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (competition) => {
    setIsEditing(true);
    setCurrentCompetition({
      ...competition,
      startDate: format(parseISO(competition.startDate), "yyyy-MM-dd"),
      endDate: format(parseISO(competition.endDate), "yyyy-MM-dd"),
    });
  };

  const handleUpdateCompetition = async (e) => {
    e.preventDefault();
    try {
      await competitionService.updateCompetition(currentCompetition.id, currentCompetition);
      await fetchCompetitions();
      setIsEditing(false);
      setCurrentCompetition(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCompetition = async () => {
    if (!competitionToDelete) return;
    try {
      await competitionService.deleteCompetition(competitionToDelete.id);
      setCompetitions((current) => current.filter((item) => item.id !== competitionToDelete.id));
      setCompetitionToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    try {
      await competitionService.createCompetition(newCompetition);
      await fetchCompetitions();
      setIsCreating(false);
      setNewCompetition({
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        status: "draft",
        category: ""
      });
      setActiveTab("active");
    } catch (err) {
      console.error("Error creating competition:", err);
      setError(err.message);
    }
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setNewCompetition((prev) => {
      const updated = { ...prev };
      updated[name] = value;
      return updated;
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCompetition((prev) => ({ ...prev, [name]: value }));
  };

  const fetchAllParticipants = async () => {
    try {
      if (!apiBaseUrl) {
        console.error('API Base URL is not set');
        return;
      }

      const { 
        clubs: clubsData, 
        players: playersData, 
        coaches: coachesData, 
        lifestyles: lifestylesData,
        artists: artistsData,   // NEW
        songs: songsData        // NEW
      } = await participantService.fetchParticipants();

      setClubs(clubsData);
      setPlayers(playersData);
      setCoaches(coachesData);
      setLifestyles(lifestylesData);
      setArtists(artistsData);  // NEW
      setSongs(songsData);       // NEW

      console.log('Fetched artists:', artistsData); // Debug log
      console.log('Fetched songs:', songsData);     // Debug log
    } catch (err) {
      console.error("Error fetching participants:", err);
      setError(err.message);
      setClubs([]);
      setPlayers([]);
      setCoaches([]);
      setLifestyles([]);
      setArtists([]);   // NEW
      setSongs([]);      // NEW
    }
  };

  useEffect(() => {
    if (activeTab === "manage" && apiBaseUrl) {
      fetchAllParticipants(); // Updated function name
    }
  }, [activeTab, apiBaseUrl]);

  const handleAddParticipantsToCompetition = async (competitionId, type, ids) => {
    try {
      setIsAddingParticipants(true);
      const selectedCompetition = competitions.find(c => c.id === competitionId);
      
      if (!selectedCompetition) {
        throw new Error('Competition not found');
      }

      if (selectedCompetition.status !== 'draft') {
        throw new Error('Can only add participants to draft competitions');
      }

      let participants = [];
      let selectedArray = [];
      
      switch (type) {
        case 'player':
          if (!ids.length) {
            throw new Error('No players selected');
          }
          selectedArray = ids;
          participants = ids.map(playerId => {
            const selectedPlayer = players.find(p => p.playerId === Number(playerId));
            if (!selectedPlayer) {
              console.error('Player not found:', playerId, 'Available players:', players);
              throw new Error(`Player with ID ${playerId} not found`);
            }
            return {
              competitionId: Number(competitionId),
              participantType: 'player',
              participantId: selectedPlayer.playerId
            };
          });
          break;

        case 'club':
          if (!ids.length) {
            throw new Error('No clubs selected');
          }
          selectedArray = ids;
          participants = ids.map(id => {
            const selectedClub = clubs.find(c => c.clubId === Number(id));
            if (!selectedClub) {
              console.error('Club not found:', id, 'Available clubs:', clubs);
              throw new Error(`Club with ID ${id} not found`);
            }
            return {
              competitionId: Number(competitionId),
              participantType: 'club',
              participantId: selectedClub.clubId
            };
          });
          break;

        case 'coach':
          if (!ids.length) {
            throw new Error('No coaches selected');
          }
          selectedArray = ids;
          participants = ids.map(id => {
            const selectedCoach = coaches.find(c => c.id === Number(id));
            if (!selectedCoach) {
              console.error('Coach not found:', id, 'Available coaches:', coaches);
              throw new Error(`Coach with ID ${id} not found`);
            }
            return {
              competitionId: Number(competitionId),
              participantType: 'coach',
              participantId: selectedCoach.id
            };
          });
          break;

        case 'model':
          if (!ids.length) {
            throw new Error('No lifestyle models selected');
          }
          selectedArray = ids;
          participants = ids.map(id => {
            const selectedModel = lifestyles.find(m => m.id === Number(id));
            if (!selectedModel) {
              console.error('Lifestyle model not found:', id, 'Available models:', lifestyles);
              throw new Error(`Lifestyle model with ID ${id} not found`);
            }
            return {
              competitionId: Number(competitionId),
              participantType: 'model',
              participantId: selectedModel.id
            };
          });
          break;

        case 'artist': // NEW: Handle artists
          if (!ids.length) {
            throw new Error('No artists selected');
          }
          selectedArray = ids;
          participants = ids.map(id => {
            const selectedArtist = artists.find(a => a.artist_id === Number(id));
            if (!selectedArtist) {
              console.error('Artist not found:', id, 'Available artists:', artists);
              throw new Error(`Artist with ID ${id} not found`);
            }
            return {
              competitionId: Number(competitionId),
              participantType: 'artist',
              participantId: selectedArtist.artist_id
            };
          });
          break;

        case 'song': // NEW: Handle songs
          if (!ids.length) {
            throw new Error('No songs selected');
          }
          selectedArray = ids;
          participants = ids.map(id => {
            const selectedSong = songs.find(s => s.song_id === Number(id));
            if (!selectedSong) {
              console.error('Song not found:', id, 'Available songs:', songs);
              throw new Error(`Song with ID ${id} not found`);
            }
            return {
              competitionId: Number(competitionId),
              participantType: 'song',
              participantId: selectedSong.song_id
            };
          });
          break;

        default:
          throw new Error('Invalid participant type');
      }

      console.log('Adding participants:', participants); // Debug log

      const responseData = await participantService.addParticipantsToCompetition(
        competitionId,
        participants
      );

      // Update the specific competition with new participants
      setCompetitions(prev => prev.map(c => 
        c.id === competitionId ? { ...c, participants: responseData.participants || [] } : c
      ));
      
      // Clear the selected IDs for the specific type
      switch (type) {
        case 'player':
          setSelectedPlayerIds([]);
          break;
        case 'club':
          setSelectedClubIds([]);
          break;
        case 'coach':
          setSelectedCoachIds([]);
          break;
        case 'model':
          setSelectedLifestyleIds([]);
          break;
        case 'artist':
          setSelectedArtistIds([]);
          break;
        case 'song':
          setSelectedSongIds([]);
          break;
      }
    } catch (err) {
      console.error("Error adding participants:", err);
      setError(err.message);
    } finally {
      setIsAddingParticipants(false);
    }
  };

  const handleRemoveParticipant = async (competitionId, participantId) => {
    try {
      if (!competitionId || !participantId) {
        throw new Error('Invalid competition or participant ID');
      }

      await participantService.removeParticipant(competitionId, participantId);
      
      // Refresh the competitions list to get the updated data
      await fetchCompetitions();
    } catch (err) {
      console.error('Error removing participant:', err);
      setError(err.message);
    }
  };

  if (loading)
    return <div className="text-center p-8 text-gray-400">Loading competitions…</div>;
  if (error) return <div className="text-accent-soft p-8">Error: {error}</div>;

  return (
    <div className="space-y-4">
        <PageHeader
          title="Competitions"
          description="Create competitions and manage their participants"
        />

        {/* Tabs */}
        <div className="flex gap-1 border-b border-line">
          {[
            { id: "active", label: "Active competitions" },
            { id: "create", label: "Create competition" },
            { id: "manage", label: "Manage participants" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? "border-accent text-white"
                  : "border-transparent text-gray-400 hover:text-white"
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "active" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-semibold text-white">All competitions</h2>
              <span className="text-sm text-gray-500">
                Showing {competitions.length} competition{competitions.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="space-y-4">
              {competitions.map((competition) => (
                <CompetitionCard
                  key={competition.id}
                  competition={competition}
                  onEditClick={handleEditClick}
                  onDeleteClick={setCompetitionToDelete}
                />
              ))}
            </div>
          </div>
        )}

        {competitionToDelete && (
          <ConfirmDialog
            title="Delete competition"
            message={`Delete “${competitionToDelete.title}”? This action cannot be undone.`}
            confirmLabel="Delete competition"
            onConfirm={handleDeleteCompetition}
            onCancel={() => setCompetitionToDelete(null)}
          />
        )}

        {activeTab === "create" && (
          <CreationModal
            isCreating={isCreating}
            setIsCreating={setIsCreating}
            newCompetition={newCompetition}
            handleCreateInputChange={handleCreateInputChange}
            handleCreateCompetition={handleCreateCompetition}
            categories={categories}
          />
        )}

        {activeTab === "manage" && (
          <ManageParticipantsSection
            competitions={competitions}
            selectedCompetitionId={selectedCompetitionId}
            setSelectedCompetitionId={setSelectedCompetitionId}
            selectedPlayerIds={selectedPlayerIds}
            setSelectedPlayerIds={setSelectedPlayerIds}
            selectedClubIds={selectedClubIds}
            setSelectedClubIds={setSelectedClubIds}
            selectedCoachIds={selectedCoachIds}
            setSelectedCoachIds={setSelectedCoachIds}
            selectedLifestyleIds={selectedLifestyleIds}
            setSelectedLifestyleIds={setSelectedLifestyleIds}
            selectedArtistIds={selectedArtistIds}           // NEW
            setSelectedArtistIds={setSelectedArtistIds}     // NEW
            selectedSongIds={selectedSongIds}               // NEW
            setSelectedSongIds={setSelectedSongIds}         // NEW
            players={players}
            clubs={clubs}
            coaches={coaches}
            lifestyles={lifestyles}
            artists={artists}                               // NEW
            songs={songs}                                   // NEW
            isAddingParticipants={isAddingParticipants}
            handleAddParticipantsToCompetition={handleAddParticipantsToCompetition}
            handleRemoveParticipant={handleRemoveParticipant}
          />
        )}

        {isEditing && currentCompetition && (
          <EditModal
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            currentCompetition={currentCompetition}
            handleEditInputChange={handleEditInputChange}
            handleUpdateCompetition={handleUpdateCompetition}
            categories={categories}
          />
        )}
    </div>
  );
}

export default Competitions;